import React, { useRef, useEffect, useState } from "react";
import ReactDOM from "react-dom";

export const recruitStatus = {
  A: "Active",
  B: "Offer",
  C: "Waitlist",
  D: "Hard Interview",
  E: "Soft Skills Done",
  F: "Interview Done",
  G: "Waiting for Interview",
  H: "Waiting for Call",
  U: "Call Failed",
  V: "Interview Failed",
  W: "Soft Rejected",
  X: "Hard Rejected",
  Y: "Lost but Not Fired",
  Z: "Fired by Company",
};

export const recruitStatusMeta = {
  A: {
    name: "Active",
    description: "Активный сотрудник компании",
  },
  B: {
    name: "Offer",
    description: "Офер рассматривает компания и/или кандидат",
  },
  C: {
    name: "Waitlist",
    description: "Кандидат в запасе (кадровый резерв)",
  },
  D: {
    name: "Hard interview",
    description: "Проведено hard интервью",
  },
  E: {
    name: "Soft skills done",
    description: "Показатели SMSN готовы",
  },
  F: {
    name: "Interview done",
    description: "Проведено soft интервью",
  },
  G: {
    name: "Waiting for interview",
    description: "Кандидат ожидает первую беседу",
  },
  H: {
    name: "Waiting for call",
    description: "Кандидат ожидает квалификационный звонок",
  },
  U: {
    name: "Call failed",
    description: "Звонок не состоялся или провален",
  },
  V: {
    name: "Interview Failed",
    description: "Провалено интервью",
  },
  W: {
    name: "Soft Rejected",
    description: "Мягкий отказ",
  },
  X: {
    name: "Hard Rejected",
    description: "Жёсткий отказ",
  },
  Y: {
    name: "Lost but Not Fired",
    description: "Потерян, но не уволен",
  },
  Z: {
    name: "Fired by Company",
    description: "Уволен компанией",
  },
};

export const getStatusTextColor = (code) => {
  const textColors = {
    A: "text-[#FFFFFF]",
    B: "text-[#FFFFFF]",
    C: "text-[#000000]",
    D: "text-[#3C53A1]",
    E: "text-[#237B71]",
    F: "text-[#237B71]",
    G: "text-[#994ABB]",
    H: "text-[#994ABB]",
    U: "text-[#B55E07]",
    V: "text-[#B55E07]",
    W: "text-[#D23A3A]",
    X: "text-[#D23A3A]",
    Y: "text-[#FFFFFF]",
    Z: "text-[#FFFFFF]",
  };
  return textColors[code] || "text-black";
};

export const getStatusColor = (code) => {
  const colors = {
    A: "bg-[#11734B]",
    B: "bg-[#0A53A8]",
    C: "bg-[#E0E0E0]",
    D: "bg-[#8FD2FC]",
    E: "bg-[#CCF4FF]",
    F: "bg-[#CCF4FF]",
    G: "bg-[#E6CFF2]",
    H: "bg-[#E6CFF2]",
    U: "bg-[#FFC8AA]",
    V: "bg-[#FFC8AA]",
    W: "bg-[#FFCFC9]",
    X: "bg-[#FFCFC9]",
    Y: "bg-[#B10202]",
    Z: "bg-[#B10202]",
  };
  return colors[code] || "bg-gray-500";
};

const StatusDropdown = ({ onChange, onClose, targetRef }) => {
  const dropdownMenuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);

  useEffect(() => {
    if (targetRef && targetRef.current) {
      const rect = targetRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY, // Position below the target
        left: rect.left + window.scrollX,
        width: 240, // Desired width for the status dropdown
      });
    } else {
      setMenuPosition(null); // Hide if target isn't available
    }
  }, [targetRef]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownMenuRef.current &&
        !dropdownMenuRef.current.contains(event.target)
      ) {
        // Optional: Check if the click is on the targetRef itself to prevent immediate re-closing
        // if (targetRef && targetRef.current && targetRef.current.contains(event.target)) {
        //   return;
        // }
        onClose();
      }
    };

    if (menuPosition) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose, menuPosition, targetRef]);

  if (!menuPosition) {
    return null; // Don't render anything if we can't position the menu
  }

  const statusOptions = Object.entries(recruitStatus).map(
    ([key, fullName]) => ({
      code: key,
      full_name: fullName,
    })
  );

  const dropdownContent = (
    <div
      ref={dropdownMenuRef}
      className="bg-white shadow-md rounded-md max-h-60 overflow-auto" // Removed w-[240px] as it's in style
      style={{
        position: "absolute",
        top: `${menuPosition.top}px`,
        left: `${menuPosition.left}px`,
        width: `${menuPosition.width}px`,
        zIndex: 99999, // Very high z-index for portal content
      }}
      onClick={(e) => e.stopPropagation()} // Prevent clicks inside from closing
    >
      {statusOptions.map((status) => (
        <div
          key={status.code}
          onClick={() => {
            onChange(status.code);
            onClose(); // Close after selection
          }}
          className="flex items-center gap-2 px-3 py-2 cursor-pointer hover:bg-gray-100"
        >
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-xs ${getStatusTextColor(
              status.code
            )} ${getStatusColor(status.code)}`}
            // style={{ marginLeft: 0 }} // Default Tailwind/flex behavior should be fine
          >
            {status.code}
          </div>
          <span className="text-sm flex-grow text-left">
            {status.full_name}
          </span>
        </div>
      ))}
    </div>
  );

  // Ensure a portal root element exists in the document body
  let portalRoot = document.getElementById("portal-root");
  if (!portalRoot) {
    portalRoot = document.createElement("div");
    portalRoot.id = "portal-root";
    document.body.appendChild(portalRoot);
  }

  return ReactDOM.createPortal(dropdownContent, portalRoot);
};

export default StatusDropdown;
